import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ChevronLeft, Building2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { BottomNav } from '../components/BottomNav';

const FormField = ({ label, value, onChange, placeholder, multiline, testId }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    {multiline ? (
      <textarea value={value || ''} onChange={onChange} placeholder={placeholder}
        data-testid={testId}
        rows={3} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#128C7E] resize-none" />
    ) : (
      <input value={value || ''} onChange={onChange} placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#128C7E]" />
    )}
  </div>
);

export const BusinessProfileScreen = () => {
  const navigate = useNavigate();
  const [biz, setBiz] = useState({});
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/businesses');
      const businesses = res.data.businesses || [];
      if (businesses.length > 0) { setBiz(businesses[0]); }
      else { setIsNew(true); }
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post('/businesses', biz);
        setBiz(res.data.business);
        setIsNew(false);
        toast.success('Business created!');
        navigate('/growth');
      } else {
        await api.patch(`/businesses/${biz.id}`, biz);
        toast.success('Business updated!');
      }
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleChange = (field) => (e) => setBiz(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="min-h-screen bg-[#F0F2F5] pb-20">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <div className="bg-[#128C7E] text-white px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)}><ChevronLeft size={24} /></button>
          <h1 className="text-base font-bold flex-1">Business Profile</h1>
          <button onClick={handleSave} disabled={saving} className="text-sm px-3 py-1.5 rounded-lg bg-white/20 font-medium flex items-center gap-1" data-testid="save-biz-btn">
            <Save size={14} /> {saving ? '...' : 'Save'}
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2"><Building2 size={14} className="text-[#128C7E]" /> Basic Information</h3>
            <FormField label="Business Name" value={biz.business_name} onChange={handleChange('business_name')} placeholder="e.g. Karigar Jaipur" testId="biz-name" />
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Business Type</label>
              <select value={biz.business_type || 'ecommerce'} onChange={e => setBiz(p => ({...p, business_type: e.target.value}))}
                data-testid="biz-type"
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none">
                {['ecommerce', 'retail_store', 'boutique', 'salon', 'restaurant', 'handicraft_business', 'fashion_brand', 'home_decor', 'beauty_business', 'service_business', 'other'].map(t =>
                  <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                )}
              </select>
            </div>
            <FormField label="Category" value={biz.business_category} onChange={handleChange('business_category')} placeholder="e.g. Fashion, Handicrafts" testId="biz-category" />
            <FormField label="Description" value={biz.description} onChange={handleChange('description')} placeholder="What does your business do?" multiline testId="biz-description" />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="text-sm font-bold text-slate-700">Contact & Location</h3>
            <FormField label="Phone" value={biz.phone} onChange={handleChange('phone')} placeholder="+91 9876543210" testId="biz-phone" />
            <FormField label="Email" value={biz.email} onChange={handleChange('email')} placeholder="business@example.com" testId="biz-email" />
            <FormField label="Website" value={biz.website} onChange={handleChange('website')} placeholder="https://yourbusiness.com" testId="biz-website" />
            <FormField label="City" value={biz.city} onChange={handleChange('city')} placeholder="e.g. Jaipur" testId="biz-city" />
            <FormField label="State" value={biz.state} onChange={handleChange('state')} placeholder="e.g. Rajasthan" testId="biz-state" />
            <FormField label="Area" value={biz.area} onChange={handleChange('area')} placeholder="e.g. Johari Bazaar" testId="biz-area" />
          </div>

          <button onClick={handleSave} disabled={saving}
            className="w-full h-14 bg-[#128C7E] text-white rounded-xl text-base font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
            data-testid="save-biz-bottom"
          >
            <Save size={18} /> {saving ? 'Saving...' : isNew ? 'Create Business' : 'Save Changes'}
          </button>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};
